/* eslint-disable no-extra-boolean-cast */
/* eslint-disable dot-notation */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  canteens: {}, // Stores cart items separately for each canteen
  cartInfo: {}, // Stores separate totals for each canteen
  totalCartItems: 0,
  totalCartAmount: 0,
  totalCartGrandTotal: 0,
  subsidy: null,
  bulk_order_info: null,
};

/**
 * Validates and formats price value to 2 decimal places
 * @param {number|string} input - Price value to validate
 * @param {number} [default_value=0] - Default value if input is invalid
 * @returns {number} Validated price with 2 decimal places
 */
const ValidatePrice = (input, default_value = 0) => {
  const data = input === undefined || input === null || isNaN(input) ? default_value : input;
  return Number((typeof data === 'string' ? Number(data) : data).toFixed(2));
};

/**
 * Calculates cart totals including taxes and subsidies for a canteen
 * @param {Array} canteenCart - Array of cart items
 * @param {Object} canteen - Canteen object with tax rates and subsidy info
 * @returns {Object} Cart info with subtotal, taxes, and grand total
 */
export const getCartInfo = (canteenCart, canteen) => {
  let sub_total = canteenCart.reduce((sum, item) => sum + ValidatePrice(item.item_price) * ValidatePrice(item.item_qty), 0);
  let _sub_total = sub_total;
  if (canteen?.subsidyApplied > 0) {
    _sub_total = Number((sub_total - canteen?.subsidyApplied).toFixed(2));
  }
  const cartInfo = {
    sgst_percentage: Number(canteen?.sgst),
    cgst_percentage: Number(canteen?.cgst),
    igst_percentage: Number(canteen?.igst),
    cart_len: canteenCart.length,
    sub_total: ValidatePrice(sub_total),
    subsidyApplied: 0,
    cgst: ValidatePrice(_sub_total * (Number(canteen?.cgst) / 100 || 0)),
    sgst: ValidatePrice(_sub_total * (Number(canteen?.sgst) / 100 || 0)),
    igst: ValidatePrice(_sub_total * (Number(canteen?.igst) / 100 || 0)),
  };
  if (canteen?.subsidyApplied > 0) {
    cartInfo['subsidyApplied'] = canteen?.subsidyApplied;
  }
  if (canteen?.subsidy_id) {
    cartInfo['subsidy_id'] = canteen?.subsidy_id;
  }
  cartInfo.total = ValidatePrice(sub_total + cartInfo.cgst + cartInfo.sgst + cartInfo.igst);
  return cartInfo;
};

/**
 * Redux slice for shopping cart state
 * Manages multi-canteen cart with separate totals, subsidies, and order notes
 */
const CartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /** Initializes cart for a specific canteen */
    setCartCanteen(state, action) {
      const { canteen_id, canteen } = action.payload;
      if (!state.canteens[canteen_id]) {
        state.canteens[canteen_id] = { cart: [], canteen, order_note: '', slot_id: canteen?.slot_id || null, delivery_date: canteen?.delivery_date || null };
        state.cartInfo[canteen_id] = getCartInfo([], canteen);
        // console.log('slot_id', state.canteens[canteen_id]?.slot_id);
      }
    },
    /** Removes all cart items for a canteen */
    removeCanteenCartItems(state, action) {
      const { canteen_id, canteen } = action.payload;
      if (!state.canteens[canteen_id]) {
        state.canteens[canteen_id] = { cart: [], canteen, order_note: '', slot_id: null, delivery_date: null };
        state.cartInfo[canteen_id] = getCartInfo([], canteen);
      }
    },
    /** Sets pre-order delivery date for a canteen */
    setCartCanteenPreOrderDate(state, action) {
      const { canteen_id, delivery_date } = action.payload;
      if (state.canteens?.[canteen_id]) {
        state.canteens[canteen_id]['delivery_date'] = delivery_date || null;
      }
    },
    /** Sets time slot for a canteen order */
    setCartCanteenSlot(state, action) {
      const { canteen_id, slot_id } = action.payload;
      if (state.canteens?.[canteen_id]) {
        state.canteens[canteen_id]['slot_id'] = slot_id || null;
        // console.log('slot_id', slot_id);
      }
    },
    /** Clears entire cart or specific canteen cart */
    resetCart(state, action) {
      return CartSlice.getInitialState();
    },
    /** Sets order note/special instructions for a canteen */
    setCartOrderNote(state, action) {
      const { canteen_id, order_note } = action.payload;
      // console.log('order_note', order_note);
      if (state.canteens[canteen_id]) {
        state.canteens[canteen_id]['order_note'] = order_note;
      }
    },
    /** Sets bulk order information */
    setBulkOrderInfo(state, action) {
      state.bulk_order_info = action.payload;
    },
    /** Sets delivery date */
    setDeliveryDate(state, action) {
      state.delivery_date = action.payload;
    },
    /** Updates cart item quantity (increment/decrement) */
    updateCart(state, action) {
      const { item, type, canteen_id } = action.payload;
      if (!state.canteens[canteen_id]) { return; }
      const cart = state.canteens[canteen_id].cart;
      const index = cart.findIndex(x => x.item_id === item.item_id);

      if (index === -1) {
        if (item.item_status) {
          cart.push({ ...item, item_qty: 1 });
          state.totalCartItems += 1;
          state.totalCartAmount += Number(item.item_price);
        }
      } else {
        if (type === 'dec') {
          cart[index].item_qty -= 1;
          state.totalCartItems -= 1;
          state.totalCartAmount -= Number(item.item_price);
          if (cart[index].item_qty === 0) {
            cart.splice(index, 1);
          }
        } else {
          cart[index].item_qty += 1;
          state.totalCartItems += 1;
          state.totalCartAmount += Number(item.item_price);
        }
      }
      console.log('Updated Cart:', JSON.stringify(cart));
      if (cart?.length === 0) {
        delete state?.canteens[canteen_id];
        delete state?.cartInfo[canteen_id];
      } else {
        state.canteens[canteen_id]['cart'] = cart;
        state.cartInfo[canteen_id] = getCartInfo(cart, state.canteens[canteen_id].canteen);
        console.log('Cart Info:', JSON.stringify(state.cartInfo[canteen_id]));
        console.log('Canteen Info:', JSON.stringify(state.canteens[canteen_id].canteen));
      }
      const total = Object.values(state.cartInfo).reduce((sum, _cart) => sum + _cart.total, 0);
      state.totalCartGrandTotal = Number(total.toFixed(2));
    },
    /** Adds item with specific quantity to cart (for repeat orders) */
    setCartItem(state, action) {
      const { item, canteen_id } = action.payload;
      if (!state.canteens[canteen_id]) { return; }
      const cart = state.canteens[canteen_id].cart;
      cart.push({ ...item, item_qty: item['item_qty'] });
      state.totalCartItems += item['item_qty'];
      state.totalCartAmount += Number(item.item_price) * Number(item['item_qty']);
      state.canteens[canteen_id]['cart'] = cart;
      state.cartInfo[canteen_id] = getCartInfo(cart, state.canteens[canteen_id].canteen);
      const total = Object.values(state.cartInfo).reduce((sum, _cart) => sum + _cart.total, 0);
      state.totalCartGrandTotal = Number(total.toFixed(2));
    },
    /** Applies subsidy discount to cart items */
    updateCartSubsidy(state, action) {
      state.subsidy = { ...action.payload };
      const { totalSubsidy, appliedSubsidyIds } = action.payload;
      for (const canteen_id in appliedSubsidyIds) {
        const cart = state.canteens[canteen_id].cart;
        state.canteens[canteen_id]['canteen']['subsidyApplied'] = appliedSubsidyIds[canteen_id]['subsidyApplied'];
        state.canteens[canteen_id]['canteen']['subsidy_id'] = appliedSubsidyIds[canteen_id]['subsidy_id'];
        state.cartInfo[canteen_id] = getCartInfo(cart, state.canteens[canteen_id].canteen);
      }
      const total = Object.values(state.cartInfo).reduce((sum, _cart) => sum + _cart.total, 0);
      state.totalCartGrandTotal = Number(total.toFixed(2));
    },
    resetCartSubsidy(state, action) {
      state.subsidy = null;
    },
  },
});

export const {
  setCartCanteen, setCartItem, updateCart, resetCart, setCartOrderNote, setDeliveryDate, setCartCanteenPreOrderDate, updateCartSubsidy, resetCartSubsidy, setBulkOrderInfo, setCartCanteenSlot, removeCanteenCartItems,
} = CartSlice.actions;
export default CartSlice.reducer;
